/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import { ReactElement, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import {
  DownloadIcon,
  RosterIcon,
  UploadFileIcon,
  UploadIcon,
} from 'src/v3/core/icons'

import theme from 'src/theme'
import axios, { pluckData } from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import SelectLinkedService from 'src/v3/manage/components/linked-service'
import { maxListWidth } from 'src/v3/manage/constants'
import { downloadFile } from 'src/v3/utilities/filesystem'
import Loading from 'src/v3/components/loading'
import {
  exportLinkedServiceRoster,
  selectActiveLinkedService,
  selectUsersByLinkedService,
  selectUsersStatus,
  fetchUsers,
} from '../manage.slice'
import { UserList } from '../components/entity-list'


let inviteUser = (email: string, linkedServiceID: string) =>
  axios.post<unknown>(
    `/api/v2/manage/linked-service/invite?linkedServiceID=${linkedServiceID}`,
    { userEmail: email },
  )

let inviteUsers = (csvFile: FormData, linkedServiceID: string) =>
  axios.post<unknown>(
    `/api/v2/manage/linked-service/invite/bulk?linkedServiceID=${linkedServiceID}`,
    csvFile,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let [inviting, setInviting] = useState(false)
  let [uploading, setUploading] = useState(false)
  let [formOpen, setFormOpen] = useState<boolean>(false)
  let [successMessage, setSuccessMessage] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')

  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let users = useAppSelector(selectUsersByLinkedService(activeLinkedService))
  let usersStatus = useAppSelector(selectUsersStatus)

  let form = useFormik({
    validationSchema: yup.object({
      email: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      email: '',
    },
    onSubmit: (values) => {
      setInviting(true)
      inviteUser(values.email, activeLinkedService)
        .then(() =>
          dispatch(fetchUsers())
            .then(() => setFormOpen(false))
            .then(() => setInviting(false))
            .then(() =>
              setSuccessMessage(`An invite has been sent to ${values.email}!`),
            )
            .then(() => form.resetForm()),
        )
        .catch((err) => {
          setFormOpen(false)
          setInviting(false)
          setErrorMessage(
            err?.response?.data || err?.message || 'Error Sending Invite',
          )
          dispatch(fetchUsers())
        })
    },
  })

  useEffect(() => {
    if (usersStatus === 'uninitialized') {
      dispatch(fetchUsers())
    }
  }, [usersStatus])

  useEffect(() => {
    if (!formOpen) {
      form.resetForm()
    }
  }, [formOpen])

  let handleBulkUpload = (event: any) => {
    let input = event.target.files[0]
    let csvData = new FormData()
    csvData.append('csvFile', input)
    setUploading(true)

    inviteUsers(csvData, activeLinkedService)
      .then(() => {
        // eslint-disable-next-line no-alert
        window.alert('CSV uploaded successfully!')
      })
      .then(() => dispatch(fetchUsers()))
      .then(() => setUploading(false))
      .catch((err) => {
        setUploading(false)
        setErrorMessage(err?.response?.data || err?.response?.status)
      })
  }

  return (
    <>
      <div className="flex items-center justify-center my-2">
        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
          <RosterIcon />
        </Avatar>
        <Typography
          variant="h4"
          className="pl-2"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Roster
        </Typography>
      </div>
      <Typography paragraph textAlign="center" color="text.secondary">
        Manage current and invited users
      </Typography>
      <SelectLinkedService linkedServiceID={activeLinkedService} />
      <div className="flex flex-wrap justify-center" style={{ gap: 15 }}>
        <Card
          variant="outlined"
          sx={{
            p: 2,
            height: 'fit-content',
            minWidth: 360,
            maxWidth: maxListWidth,
            bgcolor: 'background.paper',
            flex: '1 1 0px',
          }}
        >
          <div className="flex items-center justify-center my-2">
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
              <RosterIcon />
            </Avatar>
            <Typography
              variant="h5"
              className="pl-2"
              style={{
                fontFamily: 'smileAndWave',
              }}
            >
              Users
            </Typography>
          </div>
          <Typography paragraph textAlign="center" color="text.secondary">
            View and manage this linked service's {users.length} users.
            <br />
            <br />
            Users who have not signed in yet will show up as their email
            address. Filter by "@" to view which users were added but have not
            signed in.
          </Typography>
          <UserList users={users} route="/manage/roster" />
          <Typography variant="h6" textAlign="center" className="mt-3">
            Export User List
          </Typography>
          <div className="flex justify-center mt-2 mb-1">
            <Button
              startIcon={<DownloadIcon />}
              variant="outlined"
              size="large"
              onClick={() =>
                dispatch(exportLinkedServiceRoster(activeLinkedService))
                  .catch((err) =>
                    setErrorMessage(
                      `CSV Export Failed: ${err.response?.data || err}`,
                    ),
                  )
              }
            >
              Download CSV
            </Button>
          </div>
          <Typography variant="h6" textAlign="center" className="mt-3">
            Invite Users
          </Typography>
          <div className="flex justify-center mt-2 mb-1">
            <Button
              startIcon={<UploadIcon />}
              color="primary"
              variant="contained"
              size="large"
              component="span"
              onClick={() => setFormOpen(true)}
            >
              Invite User
            </Button>
            <label
              style={{ cursor: 'pointer', marginBottom: 0, marginLeft: 5 }}
            >
              <Button
                startIcon={<UploadFileIcon />}
                color="primary"
                variant="outlined"
                size="large"
                component="span"
              >
                <input
                  accept=".csv"
                  style={{ display: 'none' }}
                  type="file"
                  onChange={handleBulkUpload}
                />
                Upload CSV
              </Button>
            </label>
            <Dialog
              onClose={() => setFormOpen(false)}
              open={formOpen && !inviting}
            >
              <Paper
                className="px-3 py-1"
                sx={{ minWidth: 300, maxWidth: 500 }}
              >
                <form onSubmit={form.handleSubmit}>
                  <DialogTitle sx={{ textAlign: 'center', paddingBottom: 0 }}>
                    Invite User
                  </DialogTitle>
                  <DialogContent className="px-2">
                    <TextField
                      fullWidth
                      className="mt-2"
                      variant="outlined"
                      size="small"
                      value={form.values.email}
                      label="Email"
                      name="email"
                      onChange={form.handleChange}
                      error={form.touched.email && !!form.errors.email}
                    />
                  </DialogContent>
                  <DialogActions className="flex justify-center pt-0 pb-3">
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      sx={{ flex: '1 1 0px' }}
                    >
                      Invite
                    </Button>
                    <Button
                      onClick={() => setFormOpen(false)}
                      color="error"
                      variant="contained"
                      sx={{ flex: '1 1 0px' }}
                    >
                      Cancel
                    </Button>
                  </DialogActions>
                </form>
              </Paper>
            </Dialog>
          </div>
        </Card>
        <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
          <Alert
            variant="filled"
            onClose={() => setErrorMessage('')}
            severity="error"
          >
            <strong>Error: </strong>
            {errorMessage}
          </Alert>
        </Snackbar>
        <Snackbar open={!!successMessage} onClose={() => setSuccessMessage('')}>
          <Alert
            variant="filled"
            onClose={() => setSuccessMessage('')}
            severity="success"
          >
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
      <Loading entities={[usersStatus]} loading={inviting || uploading} />
    </>
  )
}
